import { useState } from 'react'

export default function ResearchSummary({ researchData }) {
  const [isExpanded, setIsExpanded] = useState(false)

  const formatAgentName = (name) => {
    // Handle specific agent name mappings (check both raw and formatted versions)
    const nameMapping = {
      'aa_sleep_medicine': 'American Academy of Sleep Medicine',
      'Aa Sleep Medicine': 'American Academy of Sleep Medicine',
      'nih': 'NIH',
      'Nih': 'NIH',
      'pubmed': 'PubMed',
      'Pubmed': 'PubMed',
      'alzheimers_association': "Alzheimer's Association",
      'Alzheimers Association': "Alzheimer's Association",
      'alzheimers_society': "Alzheimer's Society",
      'Alzheimers Society': "Alzheimer's Society"
    }
    
    // Check if there's a specific mapping for this name (case-insensitive)
    const lowerName = name.toLowerCase()
    for (const [key, value] of Object.entries(nameMapping)) {
      if (key.toLowerCase() === lowerName || key === name) {
        return value
      }
    }
    
    // Default formatting: replace underscores with spaces and capitalize words
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
  
  if (!researchData || Object.keys(researchData).length === 0) {
    return null
  }
  
  const { keywords, db_stats, agent_progress, summary_stats } = researchData
  
  // Calculate total documents found
  const totalDocuments = summary_stats?.total_documents || 
    Object.values(agent_progress || {}).reduce((sum, agent) => sum + (agent.docs_found || 0), 0)
  
  // Get agent results
  const agentResults = Object.entries(agent_progress || {}).map(([agentName, data]) => ({
    name: formatAgentName(agentName),
    ...data
  }))
  
  return (
    <div className="mt-4 p-4 bg-base-100 border rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full"></div>
            <span className="text-sm font-medium text-success">Research Completed</span>
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-600">
            <span>📚 {db_stats?.related_found || 0} Database Documents</span>
            {summary_stats && (
              <>
                {summary_stats.documents_found !== undefined && (
                  <span>📄 {summary_stats.documents_found} New Documents</span>
                )}
                <span>🔍 {summary_stats.agents_completed} sources</span>
                {keywords?.keywords && (
                  <span>🔑 {keywords.keywords.length} keywords</span>
                )}
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary hover:underline flex items-center space-x-1"
        >
          <span>{isExpanded ? 'Hide Details' : 'Show Details'}</span>
          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Keywords */}
          {keywords && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Search Keywords</h4>
              <div className="flex flex-wrap gap-1">
                {keywords.keywords?.map((keyword, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 bg-primary bg-opacity-10 text-primary text-xs rounded"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Database Stats */}
          {db_stats && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Database Search</h4>
              <div className="text-xs text-gray-600">
                <p>Related documents found: {db_stats.related_found}</p>
                <p>Total database documents: {db_stats.db_total_docs}</p>
              </div>
            </div>
          )}
          
          {/* Agent Results */}
          {agentResults.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Sources Searched</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {agentResults.map((agent, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-xs font-medium">{agent.name}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-600">
                        {agent.docs_found || 0} docs
                      </span>
                      <div className={`w-2 h-2 rounded-full ${
                        agent.status === 'completed' ? 'bg-success' : 
                        agent.status === 'started' ? 'bg-warning' : 'bg-error'
                      }`}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Summary Stats */}
          {summary_stats && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Research Summary</h4>
              <div className="text-xs text-gray-600">
                <p>Database Documents: {db_stats?.db_total_docs || summary_stats.total_documents || 0}</p>
                {summary_stats.documents_found !== undefined && (
                  <p>New Documents: {summary_stats.documents_found}</p>
                )}
                <p>Agents completed: {summary_stats.agents_completed}</p>
                {summary_stats.agents_failed > 0 && (
                  <div className="text-warning">
                    <p>Agents failed: {summary_stats.agents_failed}</p>
                    {summary_stats.failed_agent_names && summary_stats.failed_agent_names.length > 0 && (
                      <p className="text-xs mt-1">
                        Failed agents: {summary_stats.failed_agent_names.map(name => 
                          formatAgentName(name)
                        ).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}